// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/**
 * Auth Store - Memory-only token storage for W3 hybrid token architecture
 *
 * This module stores access tokens in memory only (not localStorage/sessionStorage)
 * to reduce XSS token theft risk. The refresh token is stored in an httpOnly cookie
 * by the server, which is automatically sent with requests to /api/auth/* endpoints.
 *
 * IMPORTANT: No token values should be persisted to localStorage or sessionStorage!
 */

import { API_BASE_URL } from '../api/base-url';

// Memory-only storage for access token
let accessToken: string | null = null;
let tokenExpiry: number | null = null;

// Refresh state tracking to prevent concurrent refreshes
let refreshPromise: Promise<boolean> | null = null;

// Why the last refresh was refused, when the server gave a reason worth showing.
// Only this module sees the refresh response — the API client's 401 interceptor is
// reacting to the *original* request — so the reason is recorded here (W74).
let refreshFailureReason: string | null = null;

/**
 * The reason the most recent refresh was refused, or null.
 * Currently only 'idle_timeout': the server signed the session out for inactivity,
 * which the login page explains differently from an ordinary expiry.
 */
export function lastRefreshFailureReason(): string | null {
  return refreshFailureReason;
}

/**
 * Store access token in memory with expiry tracking
 * @param token The access token string
 * @param expiresIn Seconds until token expires
 */
export function setAccessToken(token: string, expiresIn: number): void {
  accessToken = token;
  // Calculate expiry timestamp with a small buffer (30s) to refresh before actual expiry
  tokenExpiry = Date.now() + (expiresIn - 30) * 1000;
}

/**
 * Get access token if valid, null if expired or not set
 */
export function getAccessToken(): string | null {
  if (!accessToken) {
    return null;
  }

  // Check if token has expired (with buffer)
  if (tokenExpiry && Date.now() > tokenExpiry) {
    // Token expired, clear it
    accessToken = null;
    tokenExpiry = null;
    return null;
  }

  return accessToken;
}

/**
 * Clear access token from memory
 */
export function clearAccessToken(): void {
  accessToken = null;
  tokenExpiry = null;
}

/**
 * Check if we have a potentially valid access token
 */
export function hasAccessToken(): boolean {
  return getAccessToken() !== null;
}

/**
 * Check if the access token is about to expire (within 60 seconds)
 */
export function isTokenExpiringSoon(): boolean {
  if (!tokenExpiry) return true;
  return Date.now() > tokenExpiry - 60 * 1000;
}

/**
 * Refresh the access token using the httpOnly refresh cookie
 * Returns true if refresh succeeded, false otherwise
 *
 * This function deduplicates concurrent refresh attempts
 */
export async function refreshAccessToken(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshFailureReason = null;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Send httpOnly cookie
      });

      if (!response.ok) {
        // Read the reason before discarding the response; a body that is missing or
        // not JSON is normal for a gateway error and must not break sign-out.
        try {
          const body = await response.json();
          const message = String(body?.error ?? body?.message ?? '');
          if (message.includes('idle_timeout')) {
            refreshFailureReason = 'idle_timeout';
          }
        } catch {
          // no readable body — no reason to report
        }
        clearAccessToken();
        return false;
      }

      const data = await response.json();
      if (data.access_token && data.expires_in) {
        setAccessToken(data.access_token, data.expires_in);
        return true;
      }

      return false;
    } catch {
      clearAccessToken();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Logout - clear local state and call server logout endpoint
 * The server will clear the httpOnly refresh cookie
 */
export async function logout(): Promise<void> {
  clearAccessToken();

  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include', // Send httpOnly cookie so server can clear it
    });
  } catch {
    // Ignore logout errors - local state is already cleared
  }
}
